import { useEffect, useRef } from 'react';
import {
  createVelocityTracker, rubberband, project, durationFor, tween, readTranslate, prefersReducedMotion,
} from './motion.js';

// Swipe-to-dismiss für Bottom-Sheets — "follow the finger" wie iOS.
//
//  • Ziehen nach unten am Griff/Kopf ODER über den Inhalt, sobald dessen Scroll-Container
//    ganz oben steht (scrollTop === 0). Ist der Inhalt gescrollt, scrollt er normal.
//  • Bereiche mit [data-no-sheet-drag] (NumPad, Slider) und Eingabefelder starten nie einen Drag.
//  • 1:1-Tracking per rAF direkt auf transform (kein React-State pro Move), Rubber-Band nach oben,
//    Backdrop-Opacity folgt der Position, Release velocity-basiert, unterbrechbar.
//  • Rein additiv: Sheet-State (open/onOpenChange) und notifySheetOpened/Closed bleiben beim Sheet.
//
// Nutzung: const { sheetRef, backdropRef } = useSheetDrag({ open, onClose });
export function useSheetDrag({ open, onClose, disabled = false }) {
  const sheetRef = useRef(null);
  const backdropRef = useRef(null);
  const onCloseRef = useRef(onClose);
  useEffect(() => { onCloseRef.current = onClose; });

  useEffect(() => {
    if (!open || disabled) return undefined;
    const sheet = sheetRef.current;
    if (!sheet) return undefined;

    let y = 0;
    let raf = 0;
    let anim = null;
    let closing = false;
    let suppressClick = false;
    const tracker = createVelocityTracker();
    const g = { id: null, startX: 0, startY: 0, base: 0, decided: false, claimed: false, scroller: null, grabbed: false };

    const height = () => sheet.offsetHeight || window.innerHeight;
    const paint = () => {
      raf = 0;
      sheet.style.transform = y ? `translate3d(0, ${y}px, 0)` : '';
      const bd = backdropRef.current;
      if (bd) bd.style.opacity = y > 0 ? String(Math.max(0, 1 - y / height())) : '';
    };
    const schedule = () => { if (!raf) raf = requestAnimationFrame(paint); };

    const findScroller = (target) => {
      for (let el = target; el && el !== sheet; el = el.parentElement) {
        const oy = getComputedStyle(el).overflowY;
        if ((oy === 'auto' || oy === 'scroll') && el.scrollHeight > el.clientHeight + 1) return el;
      }
      return null;
    };

    const claim = (e) => {
      g.claimed = true;
      try { sheet.setPointerCapture(e.pointerId); } catch { /* ignore */ }
      sheet.style.willChange = 'transform';
      sheet.style.transition = 'none';
    };

    const onPointerDown = (e) => {
      if (closing) return;
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      const t = e.target;
      if (t.closest?.('[data-no-sheet-drag], input, textarea, select, [contenteditable="true"]')) return;
      const scroller = findScroller(t);
      const grabbed = !!anim || (sheet.getAnimations?.().length > 0);
      if (!grabbed && scroller && scroller.scrollTop > 0) return; // Inhalt scrollt normal
      // Laufende Animation greifen: vom sichtbaren Wert weitermachen (kein Sprung)
      if (anim) { y = anim.stop(); anim = null; }
      else if (grabbed) {
        y = readTranslate(sheet).y;
        sheet.getAnimations().forEach(a => a.cancel());
        paint();
      }
      Object.assign(g, { id: e.pointerId, startX: e.clientX, startY: e.clientY, base: y, decided: grabbed, claimed: false, scroller, grabbed });
      tracker.reset();
      tracker.add(y);
      if (grabbed) claim(e);
    };

    const onPointerMove = (e) => {
      if (g.id !== e.pointerId) return;
      const dx = e.clientX - g.startX;
      const dy = e.clientY - g.startY;
      if (!g.decided) {
        if (Math.abs(dx) < 4 && Math.abs(dy) < 4) return;
        g.decided = true;
        // Achsen-Lock: horizontal → nicht unsere Geste; nach oben im Scroll-Inhalt → Scroll
        if (Math.abs(dx) > Math.abs(dy) || (dy < 0 && g.scroller)) { g.id = null; return; }
        claim(e);
      }
      if (!g.claimed) return;
      const raw = g.base + dy;
      y = raw < 0 ? rubberband(raw, height()) : raw;
      tracker.add(raw);
      schedule();
    };

    const onPointerUp = (e) => {
      if (g.id !== e.pointerId) return;
      g.id = null;
      if (!g.claimed) return;
      g.claimed = false;
      suppressClick = true;
      setTimeout(() => { suppressClick = false; }, 0);
      const v = e.type === 'pointercancel' ? 0 : tracker.velocity();
      const h = height();
      const close = v > 0.5 || (y + project(v) > h * 0.45 && v > -0.1);
      const target = close ? h : 0;
      const reduced = prefersReducedMotion();
      const duration = reduced ? 0 : durationFor(target - y, v, close ? { min: 160, max: 320 } : {});
      if (close) closing = true;
      anim = tween({
        from: y, to: target, duration,
        onUpdate: (val) => { y = val; paint(); },
        onDone: () => {
          anim = null;
          sheet.style.willChange = '';
          if (close) onCloseRef.current?.();
        },
      });
    };

    // Native Scroll nur unterbinden, solange WIR die Geste halten.
    const onTouchMove = (e) => { if (g.claimed) e.preventDefault(); };
    // Nach einem Drag keinen Klick auf das Element unter dem Finger auslösen.
    const onClickCapture = (e) => { if (suppressClick) { e.stopPropagation(); e.preventDefault(); } };

    sheet.addEventListener('pointerdown', onPointerDown);
    sheet.addEventListener('pointermove', onPointerMove);
    sheet.addEventListener('pointerup', onPointerUp);
    sheet.addEventListener('pointercancel', onPointerUp);
    sheet.addEventListener('touchmove', onTouchMove, { passive: false });
    sheet.addEventListener('click', onClickCapture, true);
    return () => {
      if (raf) cancelAnimationFrame(raf);
      anim?.stop();
      sheet.removeEventListener('pointerdown', onPointerDown);
      sheet.removeEventListener('pointermove', onPointerMove);
      sheet.removeEventListener('pointerup', onPointerUp);
      sheet.removeEventListener('pointercancel', onPointerUp);
      sheet.removeEventListener('touchmove', onTouchMove);
      sheet.removeEventListener('click', onClickCapture, true);
      sheet.style.transform = '';
      sheet.style.willChange = '';
      if (backdropRef.current) backdropRef.current.style.opacity = '';
    };
  }, [open, disabled]);

  return { sheetRef, backdropRef };
}
